/*
 * at-app.js — the /autotune page: a recording in, the same voice in tune out,
 * optionally over its backing track.
 *
 * Pipeline: decode (the shared gate) → pitch every 10 ms (at-pitch.js, in
 * the worker) → AutotuneCore (the singer's own A, the key, the notes, the
 * target pitch) → AutotunePsola in the worker (the voice itself, re-spaced)
 * → AutotuneFx (the robot, the extra voices). The graph shows the sung pitch,
 * the tuned pitch and the notes; a note can be moved or left as sung, and
 * every change re-renders only the synthesis — the analysis runs once per file.
 *
 * The backing track («минус») is optional: its key and its own A (in cents
 * from 440) become the grid the voice lands on — a beat a quarter-tone off is
 * common, and a voice tuned to 440 over it is in tune and still sounds wrong.
 * It plays under the voice and can go into the file.
 *
 * Sound presets are one choice each a full chain: Natural, Hard (the instant
 * jump), Robot (a vocoder singing the tuned line), Double, and — Premium —
 * Harmony and Choir. Everyone can LISTEN to every preset; the download of a
 * Premium one asks for the plan or offers the same voice without the extra
 * voices.
 *
 * Billing: one unit per FILE, on download. The pick asks the cap first (a
 * spent day shows the wall before minutes of analysis) and closes that ticket
 * when the editor opens; the download takes a fresh ticket for the same file
 * and commits it — a second download of the same file today is free, the mix
 * and the voice alike.
 * ES2019.
 */
(function () {
  'use strict';

  var CFG = window.__AT_CFG || {};
  var I18N = CFG.i18n || {};
  var SLUG = 'autotune';
  var Core = window.AutotuneCore;

  function t(k, vars) {
    var s = I18N[k] != null ? String(I18N[k]) : k;
    if (vars) Object.keys(vars).forEach(function (v) { s = s.split(':' + v).join(String(vars[v])); });
    return s;
  }
  function $(id) { return document.getElementById(id); }
  function show(el, on) { if (el) el.hidden = !on; }
  function has(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
  function ae(name, data) { try { if (typeof window.__ae === 'function') window.__ae(name, Object.assign({ slug: SLUG }, data || {})); } catch (e) { /* */ } }
  function funnel(stage, data) { try { if (typeof window.__aeFunnel === 'function') window.__aeFunnel(stage, data || {}); } catch (e) { /* */ } }
  function stage(name, meta) { try { if (typeof window.__stage === 'function') window.__stage(name, meta); } catch (e) { /* */ } }
  function toast(msg, kind) { if (typeof window.__toast === 'function') window.__toast(msg, kind || 'error'); }
  function names() { return CFG.noteNames || ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B']; }
  function noteName(m) {
    var r = Math.round(m);
    return names()[((r % 12) + 12) % 12] + (Math.floor(r / 12) - 1);
  }
  function keyLabel(tonic, mode) { return t(mode === 'minor' ? 'key_minor' : 'key_major', { note: names()[tonic] }); }
  function isCancel(err, signal) {
    return typeof window.__isUserCancel === 'function' ? window.__isUserCancel(err, signal) : !!(signal && signal.aborted);
  }
  function isPro() { return typeof window.__isProNow === 'function' ? !!window.__isProNow(false) : false; }
  function newCtx() { return new (window.AudioContext || window.webkitAudioContext)(); }

  // ------------------------------------------------------------------
  // Sound presets. `mode`/`speedMs` set the tuning; the rest are layers.
  //   double: two copies a few cents off, a few ms late, left and right —
  //           what a second take of the same line does;
  //   harmony: [{steps | semis, pan, gain, delayMs}] — see Core.harmony;
  //   robot: the vocoder on the lead (AutotuneFx.vocode).
  // `base` is the free preset a Premium one falls back to for the download.
  // ------------------------------------------------------------------
  var PRESETS = {
    natural: { mode: 'natural', speedMs: 40 },
    hard: { mode: 'effect', speedMs: 0 },
    robot: { mode: 'effect', speedMs: 0, robot: { wet: 0.85, sub: 0.35 } },
    double: { mode: 'natural', speedMs: 40, double: true },
    harmony: {
      mode: 'natural', speedMs: 40, pro: true, base: 'natural',
      harmony: [{ steps: 2, pan: -0.55, gain: 0.5, delayMs: 11 }, { steps: -3, pan: 0.55, gain: 0.45, delayMs: 7 }]
    },
    choir: {
      mode: 'natural', speedMs: 40, pro: true, base: 'double', double: true,
      harmony: [{ steps: 2, pan: -0.75, gain: 0.42, delayMs: 13 }, { steps: -3, pan: 0.75, gain: 0.4, delayMs: 9 }, { semis: -12, pan: 0, gain: 0.28, delayMs: 5 }]
    }
  };

  var S = {
    file: null, buffer: null, ctx: null,
    track: null, ref: null, refCents: 0, key: null, engine: '', backing: null,
    grid: null, notes: [], overrides: {}, out: null, land: 0, score: null, tuned: null,
    params: { mode: 'natural', strength: 1, speedMs: 40, vibrato: 1, drift: 0.3, tuneTo: 'a440', scale: 'auto', strict: false },
    preset: 'natural', monotone: false,
    songFirst: false,      // the voice was not found under the music: the editor shows the split offer alone
    phrase: null,          // the take is a typed phrase: {text, lang, voice, words} — its unit is words, not the file
    beat: null,            // {file, buffer, tonic, mode, conf, cents, gain, offset, fromSong}
    song: null,            // «Целая песня»: null | 'offer' | 'running' | 'done'; songMix — the mix it came from
    songMix: null, songEngine: '', songAbort: null,
    listen: 'mix', what: 'mix',
    worker: null, seq: 0, inFlight: 0, pending: false, renderedFor: '',
    player: null, which: 'after', pos: 0,
    ticket: null, abort: null, t0: 0, downloads: 0, run: 0
  };
  var graph = null;
  var VIEWS = ['atWork', 'atError', 'atEditor'];
  function view(id) { VIEWS.forEach(function (v) { show($(v), v === id); }); show($('atHint'), !id || id === 'atError'); }

  // ------------------------------------------------------------------
  // Intake: decode, pitch, analysis.
  // ------------------------------------------------------------------
  async function onFile(file) {
    teardown();
    // this run's token: a newer pick or a removal bumps S.run (teardown), and
    // the replaced run must not touch the page that now shows another file
    var run = S.run;
    var stale = function () { return run !== S.run; };
    var TL = window.__toolLimit;
    var phrase = file && file.__atPhrase ? file.__atPhrase : null;
    // a typed phrase was paid for in words when it was voiced; the file cap
    // is for recordings
    if (TL && !phrase) {
      var key = TL.fileKey ? TL.fileKey(file) : null;
      S.ticket = TL.begin(SLUG, key);
      if (!S.ticket) return;                  // the wall is up, by the primitive
    }
    S.file = file;
    S.phrase = phrase;
    // a typed phrase is synthesised speech, and its file is marked as such;
    // a picked recording (even split from a song) is the person's own voice
    try { if (window.__aiOutput) { if (phrase) window.__aiOutput.used(); else window.__aiOutput.reset(); } } catch (e0) { /* */ }
    S.abort = new AbortController();
    var signal = S.abort.signal;
    S.t0 = Date.now();
    view('atWork');
    phase(t('phase_decode'), 0.02);
    stage('decode');
    try {
      S.ctx = await window.__liveAudioCtxReady(S.ctx, newCtx);
      var gated = await window.AudioDecoderRobust.decodeGated(file, { slug: SLUG, ctx: S.ctx, signal: signal, multiplier: 4 });
      if (stale()) return;
      if (!gated || !gated.buffer) { closeTicket(); view(null); resetZone(); return; }
      if (signal.aborted) throw cancelled();
      S.buffer = gated.buffer;
      funnel('decoded', { seconds: Math.round(S.buffer.duration), ch: S.buffer.numberOfChannels, sr: S.buffer.sampleRate });
      stage('analyse');
      await analyse(signal);
      if (stale()) return;
      closeTicket();                          // analysis done: nothing is billed until the download
      buildGrid();
      retarget();
      stage('editing');
      view('atEditor');
      openEditor();
      funnel('editor_open', { engine: S.engine, ms: Date.now() - S.t0, phrase: phrase ? 1 : 0 });
      // a spoken phrase moves little under a natural tune: it starts on the
      // hard one (the «sung speech» sound), and the person is told the rest
      if (phrase) {
        if (S.preset === 'natural' || S.preset === 'double') setPreset('hard', true);
        toast(t('phrase_tip'), 'info');
      }
    } catch (err) {
      if (stale()) return;
      closeTicket();
      if (isCancel(err, signal)) { view(null); resetZone(); return; }
      var code = (err && (err.code || err.name)) || 'error';
      // a song whose voice the music hides: not an error — the way to the
      // voice is the split, offered where the refusal used to be
      if (code === 'NO_VOICE_SONG') { stage('song_first'); songFirst(); return; }
      ae('tool_error', { error_type: 'analyse_' + code, msg: String((err && err.message) || err).slice(0, 160) });
      stage('error', { reason: code });
      fail(err);
    }
  }
  // «Split first»: the editor opens on the song offer alone (the graph and
  // the rail wait for a voice to show); the split makes the voice the take.
  function songFirst() {
    S.songFirst = true;
    $('atEditor').classList.add('at-split-first');
    view('atEditor');
    songUi('offer');
    syncPerkBadges();
    wideSync();
    ae('feature_use', { f: 'song_first', pro: isPro() ? 1 : 0, sec: S.buffer ? Math.round(S.buffer.duration) : null });
  }
  function leaveSongFirst() {
    S.songFirst = false;
    $('atEditor').classList.remove('at-split-first');
    wideSync();
  }
  // the editor takes the wide band only while its grid shows (data-ww-when)
  function wideSync() { try { if (window.WideWorkspace) window.WideWorkspace.sync(); } catch (e) { /* */ } }
  function cancelled() { var e = new Error('cancelled'); e.name = 'AbortError'; return e; }
  function closeTicket() { if (S.ticket) { try { S.ticket.abort(); } catch (e) { /* */ } S.ticket = null; } }

  function phase(text, frac) {
    $('atPhase').textContent = text;
    var p = Math.max(0, Math.min(1, frac || 0));
    $('atBar').style.width = (p * 100).toFixed(1) + '%';
    $('atBarWrap').setAttribute('aria-valuenow', String(Math.round(p * 100)));
  }

  function fail(err) {
    var code = err && (err.code || err.name);
    var msg = t('err_analyse');
    if (code === 'NO_VOICE') msg = t('err_no_voice');
    else if (code === 'NO_VOICE_SPLIT') msg = t('err_no_voice_split');
    else if (code === 'NO_VOICE_PHRASE') msg = t('err_no_voice_phrase');
    else if (code === 'InvalidFormatError' || code === 'DECODE' || code === 'EncodingError') msg = t('err_decode');
    $('atErrorText').textContent = msg;
    view('atError');
  }

  // Pitch every 10 ms, in the worker (at-pitch.js: two-pass YIN — why not
  // the neural tracker is written there, with the measurement).
  async function analyse(signal) {
    phase(t('phase_pitch'), 0.05);
    await initWorker(signal);
    if (signal.aborted) throw cancelled();
    // A sung take is tracked strictly: a frame counts as pitch only under a
    // clear dip (0.15), so breaths and room noise make no notes (at-pitch.js).
    // A typed phrase has neither — it is a synthetic voice with no room — and
    // the strict dip refused its short words: of 78 prod voicings of one or
    // two words (24.10.2026, Edge and Silero voices) 10 had under 0.15 s of
    // pitch («Да», «Эй», «Привет»: 0.06–0.14 s; the voices' vowels dip only
    // to 0.25–0.5). With a dip of 0.3 and a floor of 0.1 s one is left —
    // a creaky voice («Привет», Богдан: no period at any dip).
    var dip = S.phrase ? 0.3 : 0.15;
    var r = await call({ type: 'pitch', opts: { threshold: dip, fmin: 70, fmax: 1100 } }, signal, function (p) {
      phase(t('phase_pitch'), 0.05 + 0.9 * p);
    });
    if (signal.aborted) throw cancelled();
    S.engine = 'yin2';
    phase(t('phase_notes'), 0.97);
    // a period YIN is sure of has CMND under the dip: confidence above 0.7
    S.track = Core.prepare(r.f0, r.conf, r.hop, { minConf: 0.5 });
    var voicedSec = 0;
    for (var j = 0; j < S.track.n; j++) if (S.track.voiced[j]) voicedSec += r.hop;
    var trackMsg = function () { return { type: 'track', track: { hop: S.track.hop, n: S.track.n, voiced: S.track.voiced, midi: S.track.midi } }; };
    // a sung take needs a second of steady pitch to be a take at all; a typed
    // phrase is speech, and its words carry a few tenths of a second of pitch
    if (voicedSec < (S.phrase ? 0.1 : 1)) {
      var e1 = new Error('no voice');
      e1.code = S.phrase ? 'NO_VOICE_PHRASE' : (S.songMix ? 'NO_VOICE_SPLIT' : 'NO_VOICE');
      // No voice found — but in a finished song the tracker barely hears it
      // under the music (59 % of real song windows ended here): the sound that
      // fills the take tells music from noise, and music gets the split, not
      // «no singing» (at-pitch.js gapStats: buried).
      if (!S.phrase && !S.songMix) {
        var g0 = await call(trackMsg(), signal);
        if (signal.aborted) throw cancelled();
        S.backing = g0;
        funnel('no_voice', { voiced: +voicedSec.toFixed(2), share: g0.voicedShare != null ? +g0.voicedShare.toFixed(2) : null, lflat: g0.loudFlatDb != null ? Math.round(g0.loudFlatDb) : null, buried: g0.buried ? 1 : 0 });
        if (g0.buried) e1.code = 'NO_VOICE_SONG';
      }
      throw e1;
    }
    var gaps = await call(trackMsg(), signal);
    if (signal.aborted) throw cancelled();
    S.backing = gaps;
    S.ref = Core.reference(S.track);
    // the singer's own A counts only when the held notes agree about it
    S.refCents = S.ref.confidence >= 0.35 ? S.ref.cents : 0;
    var chrom = Core.segment(S.track, Core.grid(0, 'chromatic', S.refCents));
    S.key = Core.detectKey(S.track, chrom, S.refCents);
    if (S.beat) S.params.tuneTo = 'beat';
    fillKeySelect();
    funnel('analysed', {
      engine: S.engine, key: Core.keyName(S.key.tonic, S.key.mode), kconf: S.key.confidence,
      ref: Math.round(S.ref.cents), refc: +S.ref.confidence.toFixed(2), voiced: Math.round(voicedSec),
      backing: S.backing && S.backing.levelDb != null ? Math.round(S.backing.levelDb) : null,
      flat: S.backing && S.backing.flatnessDb != null ? Math.round(S.backing.flatnessDb) : null,
      share: S.backing && S.backing.voicedShare != null ? +S.backing.voicedShare.toFixed(2) : null,
      lflat: S.backing && S.backing.loudFlatDb != null ? Math.round(S.backing.loudFlatDb) : null,
      music: S.backing && S.backing.music ? 1 : 0, beat: S.beat ? 1 : 0, ms: Date.now() - S.t0
    });
  }

  // ------------------------------------------------------------------
  // Settings -> notes -> target pitch -> ratios.
  // ------------------------------------------------------------------
  function keyUsable() { return S.key && S.key.confidence !== 'low'; }
  function beatKeyUsable() { return !!(S.beat && S.beat.tonic != null && S.beat.conf >= 25); }
  function currentGrid() {
    var p = S.params;
    var strict = p.strict ? 'scale' : 'soft';
    if (p.scale === 'chromatic') return Core.grid(0, 'chromatic', S.refCents);
    if (p.scale === 'auto') {
      // the backing track knows the key better than a short take does
      if (beatKeyUsable()) return Core.grid(S.beat.tonic, S.beat.mode, S.refCents, strict);
      if (!keyUsable()) return Core.grid(0, 'chromatic', S.refCents);
      return Core.grid(S.key.tonic, S.key.mode, S.refCents, strict);
    }
    var parts = p.scale.split(':');
    return Core.grid(+parts[0], parts[1], S.refCents, strict);
  }
  // The key, the scale mode or the tuning changed: the notes are found again,
  // and hand edits no longer map onto them.
  function buildGrid() {
    var hadEdits = Object.keys(S.overrides).length > 0 && !S.monotone;
    S.grid = currentGrid();
    S.notes = Core.segment(S.track, S.grid);
    S.overrides = S.monotone ? Core.monotone(S.notes) : {};
    if (graph && graph.selected >= 0) graph.select(-1);
    return hadEdits;
  }
  function params() {
    var p = S.params;
    return {
      mode: p.mode, strength: p.strength, speedMs: p.speedMs, vibrato: p.vibrato, drift: p.drift,
      tuneTo: p.tuneTo === 'beat' && !S.beat ? 'a440' : p.tuneTo, beatCents: S.beat ? S.beat.cents : 0,
      overrides: S.overrides
    };
  }
  // Everything after the notes: cheap (one pass over the frames), so it runs
  // on every move of a slider; the sound follows when the hand stops.
  function retarget() {
    if (!S.track) return;
    var pr = params();
    S.out = Core.target(S.track, S.notes, S.grid, pr);
    S.land = Core.landOf(S.grid, pr);
    S.score = {
      before: Core.score(S.track, S.notes, S.track.midi, 10, S.land),
      after: Core.score(S.track, S.notes, S.out, 10, S.land)
    };
    if (graph) {
      graph.setRows(S.grid.offset + S.land, S.grid.pcs, S.land);
      graph.setNotes(S.notes, S.overrides);
      graph.setCurve(S.out);
    }
    updateStats();
    render();
  }

  // The layers of the chosen preset, as the worker takes them.
  function fxFor(presetKey) {
    var P = PRESETS[presetKey] || PRESETS.natural;
    var fx = { robot: null, layers: [] };
    var transfer = [];
    if (P.robot) {
      var curve = S.out.slice();
      fx.robot = { curve: curve, wet: P.robot.wet, sub: P.robot.sub };
      transfer.push(curve.buffer);
    }
    function layer(curve, spec, gate) {
      var r = Core.ratios(S.track, curve);
      transfer.push(r.buffer);
      fx.layers.push({ ratios: r, pan: spec.pan, gain: spec.gain, delayMs: spec.delayMs, gate: gate });
    }
    if (P.double) {
      [[0.09, -0.8, 19], [-0.09, 0.8, 28]].forEach(function (d) {
        var c = new Float32Array(S.out.length);
        for (var i = 0; i < c.length; i++) c[i] = S.out[i] + d[0];
        layer(c, { pan: d[1], gain: 0.45, delayMs: d[2] }, false);
      });
    }
    (P.harmony || []).forEach(function (h) {
      layer(Core.harmony(S.track, S.notes, S.out, S.grid, h, S.overrides), h, true);
    });
    return { fx: fx, transfer: transfer };
  }

  var renderTimer = 0;
  var renderWaiters = [];
  function render() {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(sendRender, 120);
    busy(true);
  }
  function sendRender() {
    if (!S.out) return;
    if (!S.worker) { revive(); return; }
    if (S.inFlight) { S.pending = true; return; }      // one render at a time; the latest wins
    var ratios = Core.ratios(S.track, S.out);
    var built = fxFor(S.preset);
    S.inFlight = ++S.seq;
    S.inFlightPreset = S.preset;
    S.worker.postMessage({ type: 'render', id: S.inFlight, ratios: ratios, fx: built.fx }, [ratios.buffer].concat(built.transfer));
  }
  // Resolves when the sound on hand matches the current settings.
  function rendered() {
    if (S.tuned && !S.inFlight && !S.pending && !renderTimer) return Promise.resolve();
    return new Promise(function (resolve, reject) { renderWaiters.push({ resolve: resolve, reject: reject }); });
  }
  function settleWaiters(err) {
    var w = renderWaiters; renderWaiters = [];
    w.forEach(function (x) { if (err) x.reject(err); else x.resolve(); });
  }
  // The worker died with the editor open (a crash, a kill): the same take
  // goes into a new one — the recording, then the track — and the render
  // that was waiting goes out. Without this the page said «updating the
  // sound» forever.
  var reviving = false;
  async function revive() {
    if (reviving || !S.buffer || !S.track) return;
    reviving = true;
    var run = S.run;
    try {
      await initWorker(null);
      await call({ type: 'track', track: { hop: S.track.hop, n: S.track.n, voiced: S.track.voiced, midi: S.track.midi } }, null);
      reviving = false;
      if (run !== S.run) return;
      ae('feature_use', { f: 'worker_revived' });
      sendRender();
    } catch (e) {
      reviving = false;
      if (run !== S.run) return;
      busy(false);
      settleWaiters(e);
      toast(t('err_render'));
      ae('tool_error', { error_type: 'worker_revive', msg: String((e && e.message) || e).slice(0, 120) });
    }
  }
  function busy(on) {
    $('atRenderState').textContent = on ? t('rendering') : '';
    $('atDownload').disabled = on || !S.tuned;
  }

  // The worker gets each take once ('init'); the pitch comes back by call();
  // renders go through sendRender() (only the latest one matters).
  var waits = {};
  function call(msg, signal, onProgress) {
    return new Promise(function (resolve, reject) {
      if (!S.worker) { reject(new Error('no worker')); return; }
      var id = ++S.seq;
      msg.id = id;
      waits[id] = { resolve: resolve, reject: reject, onProgress: onProgress };
      if (signal) signal.addEventListener('abort', function () {
        // a synchronous loop in a worker stops only with the worker
        if (!waits[id]) return;
        delete waits[id];
        killWorker();
        reject(cancelled());
      }, { once: true });
      S.worker.postMessage(msg);
    });
  }
  // The worker is made while the person picks a file (its scripts load
  // then, not after the pick) and serves every take of the visit; a cancel
  // kills it and the next take makes a new one.
  var readyWait = null;
  function ensureWorker() {
    if (S.worker) return S.worker;
    var w = new Worker(CFG.workerUrl);
    S.worker = w;
    waits = {};
    S.inFlight = 0; S.pending = false;
    w.onmessage = function (ev) {
      var m = ev.data || {};
      if (m.type === 'ready') { if (readyWait) { var rw = readyWait; readyWait = null; rw.resolve(); } return; }
      var wt = waits[m.id];
      if (m.type === 'progress') { if (wt && wt.onProgress) wt.onProgress(m.p); return; }
      if (m.type === 'pitch' || m.type === 'track') { if (wt) { delete waits[m.id]; wt.resolve(m); } return; }
      if (m.type === 'error' && wt) { delete waits[m.id]; var e = new Error(m.message); e.code = 'WORKER'; wt.reject(e); return; }
      if (m.type === 'rendered' || m.type === 'error') {
        if (m.id !== S.inFlight) return;
        S.inFlight = 0;
        if (m.type === 'rendered') {
          var b = S.buffer;
          if (!b) return;
          var tuned = S.ctx.createBuffer(m.channels.length, b.length, b.sampleRate);
          for (var c = 0; c < m.channels.length; c++) tuned.copyToChannel(m.channels[c], c);
          S.tuned = tuned;
          S.renderedFor = S.inFlightPreset;
          if (S.player && S.which === 'after') restartAt(position());
        } else {
          ae('tool_error', { error_type: 'render', msg: String(m.message).slice(0, 160) });
          toast(t('err_render'));
        }
        if (S.pending) { S.pending = false; sendRender(); return; }
        renderTimer = 0;
        busy(false);
        settleWaiters(m.type === 'error' ? new Error(m.message) : null);
      }
    };
    w.onerror = function (ev) {
      if (ev && ev.preventDefault) ev.preventDefault();
      var e = new Error((ev && ev.message) || 'worker failed'); e.code = 'WORKER';
      Object.keys(waits).forEach(function (id) { waits[id].reject(e); });
      waits = {};
      if (readyWait) { var rw = readyWait; readyWait = null; rw.reject(e); }
      killWorker();
      if (S.inFlight) { S.inFlight = 0; S.pending = false; render(); }
    };
    return w;
  }
  function killWorker() {
    if (S.worker) { try { S.worker.terminate(); } catch (e) { /* */ } S.worker = null; }
  }
  function initWorker(signal) {
    return new Promise(function (resolve, reject) {
      var w = ensureWorker();
      S.inFlight = 0; S.pending = false;
      readyWait = { resolve: resolve, reject: reject };
      if (signal) signal.addEventListener('abort', function () {
        if (!readyWait) return;
        readyWait = null;
        killWorker();
        reject(cancelled());
      }, { once: true });
      var b = S.buffer, chans = [];
      for (var c = 0; c < b.numberOfChannels; c++) chans.push(b.getChannelData(c).slice());
      w.postMessage({ type: 'init', sr: b.sampleRate, channels: chans }, chans.map(function (x) { return x.buffer; }));
    });
  }

  // ------------------------------------------------------------------
  // The editor opens: the graph, the drawers (open where there is room).
  // ------------------------------------------------------------------
  function openEditor() {
    if (!graph) {
      graph = window.AutotuneGraph.create($('atGraph'), {
        noteName: noteName,
        onSeek: seek,
        onSelect: noteBar,
        onMove: moveNote,
        onSkip: skipNote,
        onView: syncScroll
      });
    }
    graph.setData({
      hop: S.track.hop, midi: S.track.midi, out: S.out, notes: S.notes, overrides: S.overrides,
      rowShift: S.grid.offset + S.land, pcs: S.grid.pcs, land: S.land, duration: S.buffer.duration
    });
    graph.setEditing($('atEdit').getAttribute('aria-pressed') === 'true');
    noteBar(-1);
    syncPerkBadges();
  }
  function syncScroll(t0, t1, full) {
    var r = $('atScroll');
    var zoomed = t1 - t0 < full - 0.01;
    show($('atScrollRow'), zoomed);
    if (zoomed) { r.max = String(Math.max(0, full - (t1 - t0))); r.step = '0.01'; r.value = String(t0); }
  }
  function gridLabel() {
    return S.grid.mode === 'chromatic' ? t('key_chromatic_short') : keyLabel(S.grid.tonic, S.grid.mode);
  }
  function landCents() { return Math.round((S.grid.offset + S.land) * 100); }
  function updateStats() {
    var sc = S.score;
    $('atHitBefore').textContent = Math.round(sc.before.within * 100) + '%';
    $('atHitAfter').textContent = Math.round(sc.after.within * 100) + '%';
    $('atHitAfter').classList.toggle('good', sc.after.within >= 0.6);
    $('atKeyStat').textContent = gridLabel();
    $('atNotesStat').textContent = String(S.notes.length);
    var lc = landCents();
    $('atRefStat').textContent = (lc > 0 ? '+' : lc < 0 ? '−' : '') + Math.abs(lc) + '¢';
    $('atKeySub').textContent = gridLabel();
    var refC = Math.round(S.ref.cents);
    var own = Math.abs(refC) >= 8 && S.ref.confidence >= 0.35;
    $('atRefHint').textContent = own
      ? t(refC > 0 ? 'ref_sharp' : 'ref_flat', { c: Math.abs(refC), hz: (440 * Math.pow(2, refC / 1200)).toFixed(1) })
      : t('ref_ok');
    // music under the voice: the offer to split it (the old warning only where
    // the offer cannot stand — a backing track already loaded by hand)
    var music = !!(S.backing && S.backing.music);
    if (music && !S.song) S.song = 'offer';
    songUi();
    show($('atBacking'), music && !!S.beat && !S.beat.fromSong && S.song !== 'done');
  }

  // ------------------------------------------------------------------
  // Notes: move, leave as sung, reset; all on one pitch.
  // ------------------------------------------------------------------
  function shown(k) {
    var nt = S.notes[k], ov = S.overrides;
    if (has(ov, k) && ov[k] === null) return null;
    return (has(ov, k) ? ov[k] : nt.target) + S.land;
  }
  function noteBar(k) {
    var on = k >= 0 && k < S.notes.length;
    $('atNoteBar').classList.toggle('idle', !on);
    ['atNoteUp', 'atNoteDown', 'atNoteSkip', 'atNoteReset'].forEach(function (id) { $(id).disabled = !on; });
    if (!on) { $('atNoteName').textContent = t('note_pick'); $('atNoteSung').textContent = ''; return; }
    var nt = S.notes[k], tg = shown(k);
    $('atNoteName').textContent = tg == null ? t('note_as_sung_short', { note: noteName(nt.center) }) : noteName(tg);
    var sung = tg == null ? 0 : Math.round((nt.center - tg) * 100);
    $('atNoteSung').textContent = tg == null ? t('note_left') : t('note_sung', { c: (sung > 0 ? '+' : sung < 0 ? '−' : '±') + Math.abs(sung) });
    $('atNoteSkip').setAttribute('aria-pressed', tg == null ? 'true' : 'false');
    $('atNoteReset').disabled = !has(S.overrides, k);
  }
  function moveNote(k, delta) {
    if (k < 0 || k >= S.notes.length) return;
    var ov = S.overrides, nt = S.notes[k];
    var cur = has(ov, k) && ov[k] !== null ? ov[k] : nt.target;
    var next = cur + delta;
    if (Math.abs(next - nt.target) < 1e-6) delete ov[k]; else ov[k] = next;
    ae('feature_use', { f: 'note_move', d: delta, touch: graph && $('atEdit').getAttribute('aria-pressed') === 'true' ? 1 : 0 });
    retarget();
    graph.select(k);
  }
  function skipNote(k) {
    if (k < 0 || k >= S.notes.length) return;
    var ov = S.overrides;
    if (has(ov, k) && ov[k] === null) delete ov[k]; else ov[k] = null;
    ae('feature_use', { f: 'note_skip' });
    retarget();
    graph.select(k);
  }
  function resetNote(k) {
    if (k < 0) return;
    delete S.overrides[k];
    retarget();
    graph.select(k);
  }
  function setMonotone(on) {
    S.monotone = !!on;
    S.overrides = on ? Core.monotone(S.notes) : {};
    $('atMonotone').setAttribute('aria-pressed', on ? 'true' : 'false');
    if (on) toast(t('monotone_on'), 'info');
    ae('feature_use', { f: 'monotone', v: on ? 1 : 0 });
    if (graph) graph.select(-1);
    retarget();
  }

  // ------------------------------------------------------------------
  // Playback: before / after from the same place, the beat under it.
  // ------------------------------------------------------------------
  function position() {
    if (!S.player) return S.pos;
    return Math.min(S.buffer.duration, S.player.offset + (S.ctx.currentTime - S.player.startedAt));
  }
  function stopSources(p) {
    [p.src, p.beat].forEach(function (s) { if (!s) return; try { s.onended = null; s.stop(); } catch (e) { /* */ } });
  }
  function stopPlayer() {
    if (S.player) {
      S.pos = position();
      stopSources(S.player);
      S.player = null;
    }
    playButton(false);
  }
  function playButton(on) {
    var b = $('atPlay');
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
    b.querySelector('.at-play-l').textContent = on ? t('pause') : t('play');
    b.querySelector('.at-play-i').textContent = on ? '❚❚' : '▶';
  }
  function restartAt(pos) {
    var buf = S.which === 'after' && S.tuned ? S.tuned : S.buffer;
    if (S.player) { stopSources(S.player); S.player = null; }
    var ctx = S.ctx;
    var off = Math.max(0, Math.min(buf.duration - 0.01, pos));
    var when = ctx.currentTime + 0.03;
    var src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(when, off);
    var beatSrc = null;
    if (S.beat && S.listen === 'mix') {
      var bOff = off + S.beat.offset;
      if (bOff < S.beat.buffer.duration) {
        beatSrc = ctx.createBufferSource();
        beatSrc.buffer = S.beat.buffer;
        var g = ctx.createGain();
        g.gain.value = S.beat.gain;
        beatSrc.connect(g); g.connect(ctx.destination);
        beatSrc.start(when, bOff);
      }
    }
    S.player = { src: src, beat: beatSrc, startedAt: when, offset: off };
    src.onended = function () {
      if (!S.player || S.player.src !== src) return;
      stopSources(S.player);
      S.player = null; S.pos = 0;
      playButton(false);
      if (graph) graph.setPlayhead(null);
    };
    playButton(true);
    requestAnimationFrame(tick);
  }
  async function togglePlay() {
    if (S.player) { stopPlayer(); return; }
    if (!S.buffer) return;
    try { S.ctx = await window.__liveAudioCtxReady(S.ctx, newCtx); } catch (e) { toast(t('err_audio')); return; }
    restartAt(S.pos >= S.buffer.duration - 0.05 ? 0 : S.pos);
    ae('feature_use', { f: 'play', which: S.which, preset: S.preset, beat: S.beat ? S.listen : 'none' });
  }
  function seek(sec) {
    S.pos = sec;
    if (S.player) restartAt(sec); else if (graph) graph.setPlayhead(sec);
    $('atTime').textContent = fmtTime(sec);
  }
  function setWhich(which) {
    S.which = which;
    segOn('[data-at-which]', 'data-at-which', which);
    if (S.player) restartAt(position());
    ae('feature_use', { f: 'ab', which: which });
  }
  function setListen(v) {
    S.listen = v;
    segOn('[data-at-listen]', 'data-at-listen', v);
    if (S.player) restartAt(position());
    ae('feature_use', { f: 'listen', v: v });
  }
  function tick() {
    if (!S.player || !graph) return;
    var p = Math.max(0, position());
    graph.follow(p);
    graph.setPlayhead(p);
    $('atTime').textContent = fmtTime(p);
    requestAnimationFrame(tick);
  }
  function fmtTime(s) {
    var m = Math.floor(s / 60), r = s - m * 60;
    return m + ':' + (r < 10 ? '0' : '') + r.toFixed(1);
  }

  // ------------------------------------------------------------------
  // The backing track: its key, its A, its level, where the voice starts.
  // ------------------------------------------------------------------
  var KEY_IDX = { C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11 };
  var beatRun = 0;
  async function onBeat(file) {
    var run = ++beatRun;
    show($('atBeatErr'), false);
    $('atBeatKey').textContent = t('beat_reading');
    show($('atBeatOn'), true);
    show($('atBeatHint'), false);
    try {
      S.ctx = await window.__liveAudioCtxReady(S.ctx, newCtx);
      var dec = await window.AudioDecoderRobust.robust(file, S.ctx, { slug: SLUG });
      if (run !== beatRun) return;
      var buf = dec && dec.buffer;
      if (!buf || !buf.duration) throw new Error('empty');
      var k = useBeat(buf, { file: file, gain: +$('atBeatGain').value / 100, offset: S.beat && !S.beat.fromSong ? S.beat.offset : 0 });
      if (S.track) { fillKeySelect(); onRegrid(); }
      ae('feature_use', { f: 'beat_loaded', sec: Math.round(buf.duration), key: k ? k.key + k.mode : null, conf: S.beat.conf, cents: S.beat.cents });
    } catch (err) {
      if (run !== beatRun) return;
      clearBeat();
      $('atBeatErr').textContent = t('beat_err');
      show($('atBeatErr'), true);
      ae('tool_error', { error_type: 'beat_decode', msg: String((err && err.message) || err).slice(0, 120) });
    }
  }
  // A backing track in hand (a file, or the music split off the song): its
  // key and its own A, measured once. Returns what the key detector said.
  function useBeat(buf, o) {
    o = o || {};
    var k = typeof window.__detectKey === 'function' ? window.__detectKey(buf) : null;
    S.beat = {
      file: o.file || null, buffer: buf, gain: o.gain != null ? o.gain : 0.8, offset: o.offset || 0, fromSong: !!o.fromSong,
      tonic: k && has(KEY_IDX, k.key) ? KEY_IDX[k.key] : null,
      mode: k && k.mode === 'Minor' ? 'minor' : 'major',
      conf: k ? k.confidence : 0,
      cents: k && isFinite(k.tuningCents) ? k.tuningCents : 0
    };
    if (S.player) stopPlayer();
    $('atBeatGain').value = String(Math.round(S.beat.gain * 100));
    $('atBeatGainVal').textContent = fmtPct(S.beat.gain * 100);
    beatUi();
    S.params.tuneTo = 'beat';
    segOn('[data-at-tune]', 'data-at-tune', 'beat');
    return k;
  }
  function beatUi() {
    var on = !!S.beat;
    if (!on) { show($('atBeatUpload'), true); show($('atBeatSong'), false); }
    show($('atBeatOn'), on);
    show($('atBeatHint'), !on);
    show($('atListenSeg'), on);
    show($('atWhatSeg'), on);
    var tb = document.querySelector('[data-at-tune="beat"]');
    if (tb) tb.hidden = !on;
    $('atTuneDesc').textContent = on ? t('tune_desc_beat') : t('tune_desc');
    if (!on) return;
    var b = S.beat;
    var cents = (b.cents > 0 ? '+' : b.cents < 0 ? '−' : '') + Math.abs(b.cents) + '¢';
    $('atBeatKey').innerHTML = '';
    var txt = beatKeyUsable() ? t('beat_key', { key: keyLabel(b.tonic, b.mode), cents: cents }) : t('beat_key_unsure', { cents: cents });
    // the key and the cents in bold, the rest as written
    txt.split(/(\*\*[^*]+\*\*)/).forEach(function (part) {
      if (/^\*\*[^*]+\*\*$/.test(part)) { var el = document.createElement('b'); el.textContent = part.slice(2, -2); $('atBeatKey').appendChild(el); }
      else if (part) $('atBeatKey').appendChild(document.createTextNode(part));
    });
    if (S.buffer) show($('atBacking'), false);
    // the music split off the song needs no zone: it is already in place
    show($('atBeatUpload'), !b.fromSong);
    show($('atBeatSong'), !!b.fromSong);
  }
  function clearBeat() {
    beatRun++;
    if (S.player) stopPlayer();
    var had = !!S.beat;
    S.beat = null;
    beatUi();
    if (S.params.tuneTo === 'beat') { S.params.tuneTo = 'a440'; segOn('[data-at-tune]', 'data-at-tune', 'a440'); }
    if (had && S.track) { fillKeySelect(); onRegrid(); }
  }

  // ------------------------------------------------------------------
  // «Целая песня»: a voice already mixed with its music. The pitch of the
  // whole sound would move, music and all — so the song is split, the voice
  // tuned, and the music put back under it untouched, at its own level.
  // Engines: config/separators.php; Kim where the device verdict says it is
  // fast (its own worker), CascadedNet elsewhere — Kim on wasm takes 8.5–14×
  // the length of the song, a phone cannot be given a whole one.
  // ------------------------------------------------------------------
  var SEPC = CFG.sep || {};
  var engineLoads = {};
  function loadEngine(url, global) {
    var ready = function () { var e = window[global]; return e && typeof e.split === 'function' ? e : null; };
    if (ready()) return Promise.resolve(ready());
    if (!engineLoads[global]) engineLoads[global] = new Promise(function (resolve) {
      var sc = document.createElement('script');
      sc.src = url;
      // properties, not attributes: an inline on*= handler would keep
      // script-src-attr 'unsafe-inline' alive (InlineHandlerBudgetTest)
      sc.onload = function () { resolve(ready()); };
      sc.onerror = function () { engineLoads[global] = null; resolve(null); };
      document.head.appendChild(sc);
    });
    return engineLoads[global];
  }
  function songUi(state) {
    if (state !== undefined) S.song = state;
    var st = S.song;
    show($('atSong'), !!st);
    if (!st) return;
    $('atSong').classList.toggle('done', st === 'done');
    show($('atSongRun'), st === 'running');
    $('atSongSplit').disabled = st === 'running';
    if (st !== 'offer') show($('atSongPerk'), false);
    // the lead follows the state, every time: a split song's «done» used to
    // stay on the next file's offer, beside a button to split it again
    $('atSongLead').textContent = st === 'done'
      ? t(S.songEngine === 'kim' ? 'song_done' : 'song_done_cascade')
      : t(S.songFirst ? 'song_novoice' : 'song_detected');
  }
  function songPhase(text, frac) {
    $('atSongPhase').textContent = text;
    var p = Math.max(0, Math.min(1, frac || 0));
    $('atSongBar').style.width = (p * 100).toFixed(1) + '%';
    $('atSongBarWrap').setAttribute('aria-valuenow', String(Math.round(p * 100)));
  }
  async function splitSong() {
    if (!S.buffer || S.song === 'running') return;
    if (!isPro()) {
      show($('atSongPerk'), true);
      ae('feature_use', { f: 'perk_upsell', what: 'song' });
      return;
    }
    var run = S.run;
    var ctl = new AbortController();
    S.songAbort = ctl;
    var signal = ctl.signal;
    var mix = S.songMix || S.buffer;
    var t0 = Date.now();
    var engine = 'cascade', res = null, reason = '';
    if (S.player) stopPlayer();
    songUi('running');
    songPhase(t('song_loading'), 0.02);
    stage('song_split');
    try {
      if (SEPC.key === 'kim') {
        var kim = await loadEngine(SEPC.kim, 'MdxStemSeparator');
        if (kim && typeof kim.defaultEngineVerdict === 'function') {
          var v = await kim.defaultEngineVerdict();
          reason = (v && v.reason) || '';
          if (v && v.ultra) engine = 'kim';
        }
      }
      if (run !== S.run) return;
      var label = function () { return t(engine === 'kim' ? 'song_splitting' : 'song_splitting_slow'); };
      // Kim's worker lane reports the download 0…0.4, then the compute 0…1 again
      // «≈ N min left»: the shared estimator (time-based EMA, says nothing
      // until it has seen enough of the run); a split takes minutes on a phone
      var E = window.__acETA;
      try { if (E) E.beginStage('at_song'); } catch (e0) { /* */ }
      var onP = function (p, phaseName) {
        var x = Math.max(0, Math.min(1, +p || 0));
        if (engine === 'kim') x = phaseName === 'inference' ? 0.4 + 0.6 * x : Math.min(0.4, x);
        var left = '';
        try {
          if (E) {
            E.updateRate('at_song', x);
            var r = E.remainingSec('at_song');
            if (r != null && isFinite(r)) left = ' · ' + (r < 60 ? t('eta_lt_min') : t('eta_min', { n: Math.ceil(r / 60) }));
          }
        } catch (e1) { /* */ }
        songPhase(label() + left, 0.03 + 0.94 * x);
      };
      if (engine === 'kim') {
        try {
          res = await window.MdxStemSeparator.split(mix, { slug: SLUG, signal: signal, lane: 'worker', onProgress: onP });
        } catch (e) {
          if (isCancel(e, signal)) throw e;
          // the precise engine did not come up: CascadedNet does the song, and
          // the person is told — a worse split under the better engine's name is not on
          ae('feature_use', { f: 'song_fallback', reason: String((e && e.code) || (e && e.message) || e).slice(0, 100) });
          toast(t('song_fallback'), 'warning');
          engine = 'cascade';
          res = null;
        }
        try { if (window.MdxStemSeparator && typeof window.MdxStemSeparator.free === 'function') window.MdxStemSeparator.free(); } catch (e2) { /* */ }
      }
      if (!res) {
        var casc = await loadEngine(SEPC.cascade, 'StemSeparator');
        if (!casc) throw new Error('engine_unavailable');
        if (run !== S.run) return;
        res = await casc.split(mix, { slug: SLUG, signal: signal, onProgress: onP });
      }
      if (run !== S.run) return;
      if (!res || !res.vocal || !res.instrumental) throw new Error('no_stems');
      ae('feature_use', { f: 'song_split', engine: engine, why: reason, ms: Date.now() - t0, sec: Math.round(mix.duration), first: S.songFirst ? 1 : 0 });
      await adoptSplit(mix, res.vocal, res.instrumental, engine, run);
    } catch (err) {
      if (run !== S.run) return;
      if (isCancel(err, signal)) { songUi('offer'); return; }
      // split, and still no voice: music without singing — said so, not
      // «the split failed»
      if (err && err.code === 'NO_VOICE_SPLIT') {
        ae('tool_error', { error_type: 'analyse_NO_VOICE_SPLIT', engine: engine, first: S.songFirst ? 1 : 0 });
        stage('error', { reason: 'NO_VOICE_SPLIT' });
        leaveSongFirst();
        songUi(null);
        fail(err);
        return;
      }
      ae('tool_error', { error_type: 'song_split', engine: engine, msg: String((err && (err.code || err.message)) || err).slice(0, 160) });
      songUi('offer');
      toast(t('song_err'));
    } finally {
      if (S.songAbort === ctl) S.songAbort = null;
    }
  }
  // The split song becomes the take: the voice is analysed afresh (its own
  // pitch, without the music the tracker heard before), the music goes under
  // it as the backing track — same length, same start, at its level in the mix.
  async function adoptSplit(mix, vocal, instrumental, engine, run) {
    songPhase(t('phase_pitch'), 0.98);
    var ctl = new AbortController();
    S.abort = ctl;
    S.songMix = mix;
    S.buffer = vocal;
    S.tuned = null; S.out = null; S.overrides = {}; S.monotone = false; S.pos = 0;
    $('atMonotone').setAttribute('aria-pressed', 'false');
    await analyse(ctl.signal);
    if (run !== S.run) return;
    S.songEngine = engine;
    useBeat(instrumental, { gain: 1, offset: 0, fromSong: true });
    S.what = 'mix';
    segOn('[data-at-what]', 'data-at-what', 'mix');
    S.listen = 'mix';
    segOn('[data-at-listen]', 'data-at-listen', 'mix');
    fillKeySelect();
    buildGrid();
    leaveSongFirst();
    songUi('done');
    retarget();
    openEditor();
    funnel('song_split', { engine: engine });
  }

  // ------------------------------------------------------------------
  // «Фраза»: no recording — a few typed words said by one of the site's
  // voices (the same two lanes /text-to-speech and /jingle-maker use), then
  // tuned like any take. The unit is the WORD (Фарид 23.10.2026: three words a
  // phrase, ten a day without Премиум): counted by the page, as the HD voices
  // are — the server's own daily character pot already bounds what it costs us.
  // A word said again (another voice, the same tag) is not counted again.
  // ------------------------------------------------------------------
  var PH = CFG.phrase || {};
  var PH_DAY_KEY = 'at_phrase_day';
  function phraseLimits() { var L = PH.limits || {}; return (isPro() ? L.pro : L.free) || { per: 3, day: 10 }; }
  function today() { var d = new Date(); return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate(); }
  function dayWords() {
    try {
      var o = JSON.parse(localStorage.getItem(PH_DAY_KEY) || 'null');
      if (o && o.d === today() && Array.isArray(o.w)) return o.w;
    } catch (e) { /* */ }
    return [];
  }
  function spendWords(words) {
    var used = dayWords();
    words.forEach(function (w) { if (used.indexOf(w) < 0) used.push(w); });
    try { localStorage.setItem(PH_DAY_KEY, JSON.stringify({ d: today(), w: used })); } catch (e) { /* */ }
  }
  // Words of a phrase, normalised (a tag said twice is one word twice). Scripts
  // written without spaces count every two characters as a word.
  function wordsOf(text, lang) {
    var clean = String(text || '').trim();
    if (!clean) return [];
    if (/^(zh|ja|th)$/.test(lang)) {
      var chars = clean.replace(/[\s\p{P}\p{S}]+/gu, '');
      var out = [];
      for (var i = 0; i < chars.length; i += 2) out.push(chars.slice(i, i + 2).toLowerCase());
      return out;
    }
    return clean.split(/\s+/).filter(function (w) { return /[\p{L}\p{N}]/u.test(w); })
      .map(function (w) { return w.toLowerCase().replace(/^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu, ''); });
  }
  function fillPhraseLangs() {
    var sel = $('atPhraseLang');
    if (!sel || sel.options.length) return;
    (PH.langs || []).forEach(function (l) {
      var o = document.createElement('option'); o.value = l.code; o.textContent = l.name; sel.appendChild(o);
    });
    var saved = '';
    try { saved = window.__toolSettings ? window.__toolSettings.load(SLUG, 'phrase_lang', '') : ''; } catch (e) { /* */ }
    sel.value = saved && (PH.voices || {})[saved] ? saved : (PH.pageLang && (PH.voices || {})[PH.pageLang] ? PH.pageLang : (sel.options[0] && sel.options[0].value));
    fillPhraseVoices();
  }
  function fillPhraseVoices() {
    var sel = $('atPhraseVoice'), lang = $('atPhraseLang').value;
    sel.innerHTML = '';
    ((PH.voices || {})[lang] || []).forEach(function (v) {
      var o = document.createElement('option');
      o.value = v.id;
      o.textContent = v.name + (v.g === 'F' ? ' ♀' : v.g === 'M' ? ' ♂' : '');
      o.setAttribute('data-lane', v.lane);
      sel.appendChild(o);
    });
    var saved = '';
    try { saved = window.__toolSettings ? window.__toolSettings.load(SLUG, 'phrase_voice_' + lang, '') : ''; } catch (e) { /* */ }
    if (saved && sel.querySelector('option[value="' + saved.replace(/"/g, '') + '"]')) sel.value = saved;
  }
  function phraseState() {
    var lim = phraseLimits();
    var lang = $('atPhraseLang').value;
    var words = wordsOf($('atPhraseText').value, lang);
    var used = dayWords();
    var fresh = words.filter(function (w, i) { return used.indexOf(w) < 0 && words.indexOf(w) === i; });
    return { lim: lim, words: words, fresh: fresh, used: used.length, left: Math.max(0, lim.day - used.length) };
  }
  function phraseUi() {
    var st = phraseState();
    var c = $('atPhraseCount');
    c.textContent = t('phrase_count', { n: st.words.length, max: st.lim.per });
    c.classList.toggle('over', st.words.length > st.lim.per);
    $('atPhraseHint').textContent = t('phrase_hint', { n: st.lim.per });
    $('atPhraseLeft').textContent = t('phrase_left', { left: st.left, cap: st.lim.day });
  }
  function phraseSay(msg, perk) {
    var err = $('atPhraseErr'), box = $('atPhrasePerk');
    show(err, !!msg && !perk);
    if (msg && !perk) err.textContent = msg;
    show(box, !!perk);
    if (perk) $('atPhrasePerkText').textContent = msg + ' ';
  }
  function csrf() { var m = document.querySelector('meta[name="csrf-token"]'); return m ? m.content : ''; }
  // One voice, either lane: the cloud's neural voices go through the
  // text-to-speech tool's own route, ours through /api/tts/synthesize.
  async function voicePhrase(text, voice, lane, lang) {
    var resp;
    if (lane === 'edge') {
      resp = await fetch(PH.ttsAjaxUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), 'X-Requested-With': 'XMLHttpRequest', 'Accept': '*/*' },
        // `format` on this lane is a stream format name, not an extension
        body: JSON.stringify({ text: text, voice: voice, rate: 0, pitch: 0, style: '', format: 'audio-24khz-96kbitrate-mono-mp3' })
      });
    } else {
      resp = await fetch(PH.synthUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json, audio/wav', 'X-CSRF-TOKEN': csrf(), 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ text: text, language: lang, voice: voice, format: 'mp3', rate: 0, pitch: 0, locale: window.__LOCALE || '' })
      });
    }
    var e;
    if (resp.redirected) { e = new Error('redirect'); e.code = 'failed'; throw e; }
    if (resp.status === 429) { e = new Error('rate'); e.code = 'rate'; throw e; }
    if (!resp.ok) { e = new Error('http ' + resp.status); e.code = 'failed'; throw e; }
    var ct = resp.headers.get('content-type') || '';
    // a queued answer: the service is hot, and saying so beats a spinner
    if (ct.indexOf('json') >= 0) { e = new Error('queued'); e.code = 'busy'; throw e; }
    var bytes = await resp.arrayBuffer();
    if (!bytes || bytes.byteLength < 200) { e = new Error('empty'); e.code = 'failed'; throw e; }
    return new Blob([bytes], { type: ct.indexOf('wav') >= 0 ? 'audio/wav' : 'audio/mpeg' });
  }
  var phraseBusy = false;
  async function sayPhrase() {
    if (phraseBusy) return;
    var st = phraseState();
    var lim = st.lim;
    var text = $('atPhraseText').value.trim();
    if (!st.words.length) { phraseSay(t('phrase_empty')); return; }
    var paid = (PH.limits || {}).pro || {};
    // the refusal names the number; a free visitor also hears what Премиум gives
    var refuse = function (msg, proLine) {
      if (isPro()) phraseSay(msg);
      else phraseSay(msg + ' ' + proLine, true);
    };
    if (st.words.length > lim.per) {
      refuse(t('phrase_too_long', { n: st.words.length, max: lim.per }), t('phrase_more_per', { plan: PH.plan || '', paid: paid.per || '' }));
      ae('feature_use', { f: 'phrase_refused', why: 'too_long', n: st.words.length });
      return;
    }
    if (st.used + st.fresh.length > lim.day) {
      refuse(t('phrase_day_over', { cap: lim.day }), t('phrase_more_day', { plan: PH.plan || '', paid: paid.day || '' }));
      ae('feature_use', { f: 'phrase_refused', why: 'day', used: st.used });
      return;
    }
    phraseSay('');
    var lang = $('atPhraseLang').value;
    var vsel = $('atPhraseVoice');
    var voice = vsel.value;
    var opt = vsel.options[vsel.selectedIndex];
    var lane = opt ? opt.getAttribute('data-lane') : 'edge';
    var btn = $('atPhraseGo');
    phraseBusy = true;
    btn.disabled = true;
    btn.textContent = t('phrase_working');
    var t0 = Date.now();
    try {
      var blob = await voicePhrase(text, voice, lane, lang);
      spendWords(st.fresh);
      phraseUi();
      var O = window.__outputName;
      var stem = (O && typeof O.sanitize === 'function' ? O.sanitize(text) : text.replace(/[\\/:*?"<>|]+/g, ' ')).slice(0, 48).trim() || t('phrase_file');
      var file = new File([blob], stem + (blob.type === 'audio/wav' ? '.wav' : '.mp3'), { type: blob.type });
      file.__atPhrase = { text: text, lang: lang, voice: voice, words: st.words.length };
      ae('feature_use', { f: 'phrase_voiced', lang: lang, lane: lane, words: st.words.length, fresh: st.fresh.length, ms: Date.now() - t0 });
      try {
        if (window.__toolSettings) { window.__toolSettings.save(SLUG, 'phrase_lang', lang); window.__toolSettings.save(SLUG, 'phrase_voice_' + lang, voice); }
      } catch (e2) { /* */ }
      // the phrase is the take now: its panel folds away, the editor takes the screen
      $('atPhrase').open = false;
      var zone = $('atUpload');
      if (zone && typeof zone.__uploaderInjectFile === 'function') zone.__uploaderInjectFile(file, 'phrase');
      else onFile(file);
    } catch (err) {
      var code = (err && err.code) || 'failed';
      phraseSay(t(code === 'rate' ? 'phrase_rate' : code === 'busy' ? 'phrase_busy' : 'phrase_err'));
      ae('tool_error', { error_type: 'phrase_' + code, lane: lane, msg: String((err && err.message) || err).slice(0, 120) });
    } finally {
      phraseBusy = false;
      btn.disabled = false;
      btn.textContent = t('phrase_go');
    }
  }
  function bindPhrase() {
    var d = $('atPhrase');
    if (!d) return;
    var opened = false;
    d.addEventListener('toggle', function () {
      if (!d.open) return;
      fillPhraseLangs();
      phraseUi();
      if (!opened) { opened = true; ae('feature_use', { f: 'phrase_open' }); }
    });
    $('atPhraseLang').addEventListener('change', function () { fillPhraseVoices(); phraseUi(); });
    $('atPhraseText').addEventListener('input', function () { phraseSay(''); phraseUi(); });
    $('atPhraseText').addEventListener('keydown', function (ev) { if (ev.key === 'Enter') { ev.preventDefault(); sayPhrase(); } });
    $('atPhraseGo').addEventListener('click', sayPhrase);
  }

  // ------------------------------------------------------------------
  // Settings.
  // ------------------------------------------------------------------
  function fillKeySelect() {
    var sel = $('atKey');
    sel.innerHTML = '';
    var k = S.key;
    var autoLabel = beatKeyUsable() ? t('key_auto_beat', { key: keyLabel(S.beat.tonic, S.beat.mode) })
      : (keyUsable() ? t('key_auto', { key: keyLabel(k.tonic, k.mode) }) : t('key_auto_unsure'));
    opt(sel, 'auto', autoLabel);
    opt(sel, 'chromatic', t('key_chromatic'));
    var g1 = group(t('key_group_major')), g2 = group(t('key_group_minor'));
    for (var tn = 0; tn < 12; tn++) { opt(g1, tn + ':major', keyLabel(tn, 'major')); opt(g2, tn + ':minor', keyLabel(tn, 'minor')); }
    sel.appendChild(g1); sel.appendChild(g2);
    sel.value = 'auto';
    S.params.scale = 'auto';
    $('atKeyHint').textContent = beatKeyUsable() ? t('key_hint_beat')
      : (!k ? '' : (!keyUsable() ? t('key_hint_low') : (k.confidence === 'medium' ? t('key_hint_medium') : t('key_hint_high'))));
    function group(label) { var g = document.createElement('optgroup'); g.label = label; return g; }
    function opt(parent, v, label) { var o = document.createElement('option'); o.value = v; o.textContent = label; parent.appendChild(o); }
  }
  function onRegrid() {
    var hadEdits = buildGrid();
    retarget();
    if (hadEdits) toast(t('edits_reset'), 'info');
  }
  function bindSlider(id, key, scale, fmt) {
    var el = $(id), out = $(id + 'Val');
    el.addEventListener('input', function () {
      S.params[key] = (+el.value) * scale;
      if (out) out.textContent = fmt(+el.value);
      if (S.track) retarget();
    });
    el.addEventListener('change', function () { ae('feature_use', { f: 'param', k: key, v: +el.value }); });
    S.params[key] = (+el.value) * scale;
    if (out) out.textContent = fmt(+el.value);
  }
  function setSlider(id, v, fmt) { $(id).value = String(v); $(id + 'Val').textContent = fmt(v); }
  function setPreset(key, quiet) {
    if (!PRESETS[key]) key = 'natural';
    var P = PRESETS[key];
    S.preset = key;
    document.querySelectorAll('[data-at-preset]').forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-at-preset') === key ? 'true' : 'false');
    });
    S.params.mode = P.mode;
    S.params.speedMs = P.speedMs;
    setSlider('atSpeed', P.speedMs, fmtMs);
    show($('atNaturalOnly'), P.mode === 'natural');
    show($('atPerkBox'), false);
    try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'preset', key); } catch (e) { /* */ }
    if (!quiet) ae('feature_use', { f: 'preset', v: key, pro: P.pro ? 1 : 0, isPro: isPro() ? 1 : 0 });
    if (S.track) retarget();
  }
  function setTuneTo(v) {
    if (v === 'beat' && !S.beat) return;
    S.params.tuneTo = v;
    segOn('[data-at-tune]', 'data-at-tune', v);
    if (S.track) retarget();
    ae('feature_use', { f: 'tune_to', v: v });
  }
  function segOn(sel, attr, v) {
    document.querySelectorAll(sel).forEach(function (b) {
      var on = b.getAttribute(attr) === v;
      b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
  function fmtPct(v) { return Math.round(v) + '%'; }
  function fmtMs(v) { return +v === 0 ? t('speed_instant') : Math.round(v) + ' ' + t('unit_ms'); }
  function syncPerkBadges() {
    var pro = isPro();
    document.querySelectorAll('.at-app [data-perk-badge]').forEach(function (el) { el.hidden = pro; });
  }

  // ------------------------------------------------------------------
  // Download: the format and the content are chosen here, after listening.
  // ------------------------------------------------------------------
  function currentFormat() {
    var b = document.querySelector('.at-fmt.on');
    return b ? b.getAttribute('data-format') : 'wav';
  }
  // The voice over the beat, as long as the longer of the two, through the
  // site's true-peak limiter (two loud tracks summed clip otherwise). The
  // offline context resamples the beat to the voice's rate.
  async function mixWithBeat(voice) {
    var b = S.beat, sr = voice.sampleRate;
    var len = Math.ceil(Math.max(b.buffer.duration, b.offset + voice.duration) * sr);
    var OC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var oc = new OC(2, len, sr);
    var bs = oc.createBufferSource(); bs.buffer = b.buffer;
    var bg = oc.createGain(); bg.gain.value = b.gain;
    bs.connect(bg); bg.connect(oc.destination); bs.start(0);
    var vs = oc.createBufferSource(); vs.buffer = voice;
    vs.connect(oc.destination); vs.start(b.offset);
    var out = await oc.startRendering();
    var L = window.MasteringLimiter;
    if (L && typeof L.limit === 'function') {
      var res = L.limit([out.getChannelData(0), out.getChannelData(1)], sr, { ceilingDb: -1 });
      var mixed = S.ctx.createBuffer(2, len, sr);
      mixed.copyToChannel(res.out[0], 0); mixed.copyToChannel(res.out[1], 1);
      return mixed;
    }
    return out;
  }
  async function download(ev, opts) {
    opts = opts || {};
    if (!S.tuned || S.inFlight) return;
    var P = PRESETS[S.preset] || PRESETS.natural;
    // a Premium preset: hear it freely, take it with the plan — or without the
    // extra voices, on the free preset underneath
    if (P.pro && !isPro() && !opts.fallback) {
      show($('atPerkBox'), true);
      ae('feature_use', { f: 'perk_upsell', what: S.preset });
      return;
    }
    var TL = window.__toolLimit;
    // synchronous, first thing in the click: the primitive paints its wall on
    // this button. A typed phrase was paid for in words when it was voiced.
    var ticket = TL && !S.phrase ? TL.begin(SLUG, TL.fileKey ? TL.fileKey(S.file) : null) : null;
    if (TL && !S.phrase && !ticket) return;
    var fmt = currentFormat();
    var btn = $('atDownload');
    btn.disabled = true;
    try {
      if (opts.fallback) {
        setPreset(P.base || 'natural');
        await rendered();
      }
      var mix = !!(S.beat && S.what === 'mix');
      var buf = mix ? await mixWithBeat(S.tuned) : S.tuned;
      if (typeof window.ensureAudioPipeline === 'function') await window.ensureAudioPipeline();
      var AP = window.AudioPipeline;
      var blob = fmt === 'mp3' ? await AP.encodeMP3(buf, { bitrate: 256, slug: SLUG }) : AP.audioBufferToWav(buf, 24);
      window.__downloadFile(blob, outputName(fmt, mix));
      if (ticket) ticket.commit();
      S.downloads++;
      stage('complete');
      try {
        if (typeof window.__aeToolComplete === 'function') window.__aeToolComplete({
          format: fmt, preset: S.preset, mode: S.params.mode, strength: Math.round(S.params.strength * 100), speed: S.params.speedMs,
          scale: S.params.scale === 'auto' || S.params.scale === 'chromatic' ? S.params.scale : 'manual',
          strict: S.params.strict ? 1 : 0, tune: S.params.tuneTo, engine: S.engine, mono: S.monotone ? 1 : 0,
          seconds: Math.round(S.buffer.duration), notes: S.notes.length, edits: Object.keys(S.overrides).length,
          hit_before: Math.round(S.score.before.within * 100), hit_after: Math.round(S.score.after.within * 100),
          music: S.backing && S.backing.music ? 1 : 0, beat: S.beat ? 1 : 0, mix: mix ? 1 : 0, n: S.downloads
        });
      } catch (e2) { /* */ }
      show($('atPerkBox'), false);
    } catch (err) {
      if (ticket) ticket.abort();
      if (!isCancel(err)) {
        toast(t('err_save'));
        ae('tool_error', { error_type: 'export_' + fmt, msg: String((err && err.message) || err).slice(0, 120) });
      }
    } finally {
      btn.disabled = !S.tuned || !!S.inFlight;
    }
  }
  // The name carries the material: exports with other settings do not
  // collide into one name (reference_constant_output_filename_reads_as_broken).
  function outputName(fmt, mix) {
    var stem = S.file.name.replace(/\.[^.]+$/, '') || 'voice';
    // the key as the page names it («Си♭ мажор», not the «A#» of the core)
    var key = S.grid.mode === 'chromatic' ? t('file_chromatic') : keyLabel(S.grid.tonic, S.grid.mode);
    var look = S.preset === 'natural' ? '' : ' ' + String(t('preset_' + S.preset)).toLowerCase();
    var tag = t('file_suffix') + ' ' + key + ' ' + Math.round(S.params.strength * 100) + look + (mix ? ' ' + t('file_mix') : '');
    var O = window.__outputName;
    if (O && typeof O.join === 'function') return O.join(stem + ' (' + tag + ')', fmt) || ('autotune.' + fmt);
    return stem + ' (' + tag + ').' + fmt;
  }

  // ------------------------------------------------------------------
  // Reset and wiring.
  // ------------------------------------------------------------------
  function teardown() {
    S.run++;
    if (S.abort) { try { S.abort.abort(); } catch (e) { /* */ } S.abort = null; }
    stopPlayer();
    clearTimeout(renderTimer); renderTimer = 0;
    settleWaiters(cancelled());
    // a render or a pitch pass still running belongs to the old take: the
    // worker is replaced rather than waited for
    if (S.inFlight || Object.keys(waits).length) killWorker();
    closeTicket();
    if (S.songAbort) { try { S.songAbort.abort(); } catch (e) { /* */ } S.songAbort = null; }
    if (S.beat && S.beat.fromSong) { S.beat = null; beatUi(); }
    S.song = null; S.songMix = null; S.songEngine = '';
    leaveSongFirst();
    songUi();
    S.phrase = null;
    S.file = null; S.buffer = null; S.track = null; S.notes = []; S.overrides = {}; S.monotone = false;
    S.out = null; S.tuned = null; S.grid = null; S.pos = 0; S.inFlight = 0; S.pending = false; S.downloads = 0;
    $('atMonotone').setAttribute('aria-pressed', 'false');
    $('atDownload').disabled = true;
    $('atRenderState').textContent = '';
    show($('atPerkBox'), false);
    if (graph) { graph.select(-1); graph.setPlayhead(null); }
  }
  // Back to «waiting for a file»: the zone's own path, which fires
  // tool-uploader:clear — and that listener tears the work down.
  function resetZone() {
    var done = false;
    try { done = !!(window.ToolUploader && window.ToolUploader.clear('atUpload')); } catch (e) { /* */ }
    if (!done) { teardown(); view(null); }
  }

  function init() {
    var up = $('atUpload');
    if (!up || !Core) return;
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(up, function (f) { onFile(f); });
    up.addEventListener('tool-uploader:clear', function () { teardown(); view(null); });
    var beatUp = $('atBeatUpload');
    if (beatUp) {
      if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(beatUp, function (f) { onBeat(f); });
      beatUp.addEventListener('tool-uploader:clear', clearBeat);
    }
    $('atCancel').addEventListener('click', function () { if (S.abort) S.abort.abort(); });
    $('atAgain').addEventListener('click', resetZone);
    $('atPlay').addEventListener('click', togglePlay);
    document.querySelectorAll('[data-at-which]').forEach(function (b) { b.addEventListener('click', function () { setWhich(b.getAttribute('data-at-which')); }); });
    document.querySelectorAll('[data-at-listen]').forEach(function (b) { b.addEventListener('click', function () { setListen(b.getAttribute('data-at-listen')); }); });
    document.querySelectorAll('[data-at-what]').forEach(function (b) {
      b.addEventListener('click', function () { S.what = b.getAttribute('data-at-what'); segOn('[data-at-what]', 'data-at-what', S.what); ae('feature_use', { f: 'export_what', v: S.what }); });
    });
    document.querySelectorAll('[data-at-preset]').forEach(function (b) { b.addEventListener('click', function () { setPreset(b.getAttribute('data-at-preset')); }); });
    document.querySelectorAll('[data-at-tune]').forEach(function (b) { b.addEventListener('click', function () { setTuneTo(b.getAttribute('data-at-tune')); }); });
    $('atKey').addEventListener('change', function () {
      S.params.scale = $('atKey').value;
      if (S.track) onRegrid();
      ae('feature_use', { f: 'key', v: S.params.scale === 'auto' || S.params.scale === 'chromatic' ? S.params.scale : 'manual' });
    });
    $('atStrict').addEventListener('change', function () {
      S.params.strict = $('atStrict').checked;
      if (S.track) onRegrid();
      ae('feature_use', { f: 'strict', v: S.params.strict ? 1 : 0 });
    });
    bindSlider('atStrength', 'strength', 0.01, fmtPct);
    bindSlider('atSpeed', 'speedMs', 1, fmtMs);
    bindSlider('atVibrato', 'vibrato', 0.01, fmtPct);
    bindSlider('atDrift', 'drift', 0.01, fmtPct);
    $('atBeatGain').addEventListener('input', function () {
      var v = +$('atBeatGain').value;
      $('atBeatGainVal').textContent = fmtPct(v);
      if (S.beat) S.beat.gain = v / 100;
      if (S.player && S.beat) restartAt(position());
    });
    $('atMonotone').addEventListener('click', function () { if (S.track) setMonotone(!S.monotone); });
    $('atSongSplit').addEventListener('click', splitSong);
    $('atSongCancel').addEventListener('click', function () { if (S.songAbort) S.songAbort.abort(); });
    $('atEdit').addEventListener('click', function () {
      var on = $('atEdit').getAttribute('aria-pressed') !== 'true';
      $('atEdit').setAttribute('aria-pressed', on ? 'true' : 'false');
      if (graph) graph.setEditing(on);
      if (on) toast(t('edit_on_hint'), 'info');
      ae('feature_use', { f: 'touch_edit', v: on ? 1 : 0 });
    });
    $('atZoomIn').addEventListener('click', function () { if (graph) graph.zoom(2); });
    $('atZoomOut').addEventListener('click', function () { if (graph) graph.zoom(0.5); });
    $('atScroll').addEventListener('input', function () { if (graph) graph.setViewStart(+$('atScroll').value); });
    $('atNoteUp').addEventListener('click', function () { if (graph) moveNote(graph.selected, 1); });
    $('atNoteDown').addEventListener('click', function () { if (graph) moveNote(graph.selected, -1); });
    $('atNoteSkip').addEventListener('click', function () { if (graph) skipNote(graph.selected); });
    $('atNoteReset').addEventListener('click', function () { if (graph) resetNote(graph.selected); });
    document.querySelectorAll('.at-fmt').forEach(function (b) {
      b.addEventListener('click', function () {
        segOn('.at-fmt', 'data-format', b.getAttribute('data-format'));
        try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'output_format', b.getAttribute('data-format')); } catch (e) { /* */ }
        ae('feature_use', { f: 'format_selected', v: b.getAttribute('data-format') });
      });
    });
    try {
      var saved = window.__toolSettings ? window.__toolSettings.load(SLUG, 'output_format', 'wav') : 'wav';
      if (saved === 'wav' || saved === 'mp3') segOn('.at-fmt', 'data-format', saved);
      var preset = window.__toolSettings ? window.__toolSettings.load(SLUG, 'preset', 'natural') : 'natural';
      setPreset(PRESETS[preset] ? preset : 'natural', true);
    } catch (e) { /* */ }
    $('atDownload').addEventListener('click', download);
    $('atDownloadLead').addEventListener('click', function (ev) { download(ev, { fallback: true }); });
    bindOffset();
    bindPhrase();
    // the two drawers stay as the person left them: closed at first, so the
    // rail is no taller than the graph beside it
    ['atKeyDrawer', 'atFineDrawer'].forEach(function (id) {
      var d = $(id);
      try { if (window.__toolSettings && window.__toolSettings.load(SLUG, id, '0') === '1') d.open = true; } catch (e) { /* */ }
      d.addEventListener('toggle', function () {
        try { if (window.__toolSettings) window.__toolSettings.save(SLUG, id, d.open ? '1' : '0'); } catch (e) { /* */ }
        ae('feature_use', { f: 'drawer', id: id, open: d.open ? 1 : 0 });
      });
    });
    document.addEventListener('keydown', function (ev) {
      if (ev.code !== 'Space' || !S.tuned || $('atEditor').hidden) return;
      if (typeof window.__keyOwnedByFocus === 'function' && window.__keyOwnedByFocus(ev)) return;
      ev.preventDefault();
      togglePlay();
    });
    window.addEventListener('pagehide', function () { stopPlayer(); });
    var warm = function () { try { ensureWorker(); } catch (e) { /* */ } };
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(warm, { timeout: 3000 });
    else setTimeout(warm, 1500);
  }
  // «The voice comes in at»: typed, not dragged — the shared time field.
  function bindOffset() {
    var bind = function () {
      if (!window.TimeField || typeof window.TimeField.bind !== 'function') return;
      window.TimeField.bind($('atBeatOffset'), {
        get: function () { return S.beat ? S.beat.offset : 0; },
        set: function (v) {
          if (!S.beat) return;
          S.beat.offset = Math.max(0, v);
          if (S.player) restartAt(position());
          ae('feature_use', { f: 'beat_offset', v: Math.round(v * 1000) });
        },
        min: 0,
        slug: SLUG, field: 'beat_offset'
      });
    };
    (window.__timeFieldQ = window.__timeFieldQ || []).push(bind);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
